import React, { useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import type { PaidHoliday, PtoPlan, PtoType } from './hrData';
import { usDate } from './licenseUtils';

interface Props {
  ptoPlans: PtoPlan[];
  ptoTypes: PtoType[];
  holidays: PaidHoliday[];
  busy: boolean;
  /** Each resolves true once saved; false keeps the input filled in (the page shows the error). */
  onAddPlan: (name: string) => Promise<boolean>;
  onRemovePlan: (id: number) => void;
  onAddType: (name: string, color: string, deductsBalance: boolean) => Promise<boolean>;
  onRemoveType: (id: number) => void;
  onAddHoliday: (date: string, name: string) => Promise<boolean>;
  onRemoveHoliday: (id: number) => void;
  /** Admin only — the license-alert section only renders when this is passed. */
  licenseAlerts?: {
    days: number;
    onChange: (days: number) => void;
  };
}

const LICENSE_ALERT_OPTIONS = [15, 30, 45, 60, 90];
const NEW_TYPE_COLOR = '#8B5CF6';

const HRSettings: React.FC<Props> = ({
  ptoPlans, ptoTypes, holidays, busy, onAddPlan, onRemovePlan, onAddType, onRemoveType, onAddHoliday, onRemoveHoliday,
  licenseAlerts,
}) => {
  const [planName, setPlanName] = useState('');
  const [typeName, setTypeName] = useState('');
  const [typeDeducts, setTypeDeducts] = useState(true);
  const [holidayName, setHolidayName] = useState('');
  const [holidayDate, setHolidayDate] = useState('');

  const addPlan = async () => {
    if (!planName.trim()) return;
    if (await onAddPlan(planName.trim())) setPlanName('');
  };

  const addType = async () => {
    if (!typeName.trim()) return;
    if (await onAddType(typeName.trim(), NEW_TYPE_COLOR, typeDeducts)) {
      setTypeName('');
      setTypeDeducts(true);
    }
  };

  const addHoliday = async () => {
    if (!holidayDate || !holidayName.trim()) return;
    if (await onAddHoliday(holidayDate, holidayName.trim())) {
      setHolidayDate('');
      setHolidayName('');
    }
  };

  return (
    <div className="hr-settings">
      <section className="hr-settings__section">
        <h3 className="hr-settings__title">PTO Plans</h3>
        <p className="hr-settings__hint">Plans are labels for now — assign one to each person from the Directory's Edit button.</p>
        <div className="hr-settings__grid">
          {ptoPlans.map(p => (
            <div key={p.id} className="hr-settings__card">
              <div className="hr-settings__card-name">{p.name}</div>
              <div className="hr-settings__card-foot">
                <span>{p.peopleCount} {p.peopleCount === 1 ? 'person' : 'people'}</span>
                <button
                  type="button"
                  className="hr-settings__delete"
                  disabled={busy}
                  onClick={() => onRemovePlan(p.id)}
                  aria-label={`Delete ${p.name}`}
                  title={p.peopleCount ? 'People on this plan will be moved to "No plan"' : undefined}
                >
                  <FiTrash2 size={12} />
                </button>
              </div>
            </div>
          ))}
          <div className="hr-settings__card hr-settings__card--new">
            <input placeholder="New plan name" maxLength={100} value={planName} onChange={e => setPlanName(e.target.value)} />
            <button type="button" disabled={busy} onClick={addPlan}>
              <FiPlus size={12} aria-hidden="true" /> Create New PTO Plan
            </button>
          </div>
        </div>
      </section>

      <section className="hr-settings__section">
        <h3 className="hr-settings__title">PTO Types</h3>
        <div className="hr-settings__grid">
          {ptoTypes.map(t => (
            <div key={t.id} className="hr-settings__card" style={{ borderTopColor: t.color, borderTopWidth: 3, borderTopStyle: 'solid' }}>
              <div className="hr-settings__card-name">{t.name}</div>
              <div className="hr-settings__card-meta">{t.deductsBalance ? 'Uses PTO balance' : "Doesn't use PTO balance"}</div>
              <div className="hr-settings__card-foot">
                <button type="button" className="hr-settings__delete" disabled={busy} onClick={() => onRemoveType(t.id)} aria-label={`Delete ${t.name}`}>
                  <FiTrash2 size={12} />
                </button>
              </div>
            </div>
          ))}
          <div className="hr-settings__card hr-settings__card--new">
            <input placeholder="New type name" maxLength={100} value={typeName} onChange={e => setTypeName(e.target.value)} />
            <label className="hr-settings__check">
              <input type="checkbox" checked={typeDeducts} onChange={e => setTypeDeducts(e.target.checked)} />
              Uses PTO balance
            </label>
            <button type="button" disabled={busy} onClick={addType}>
              <FiPlus size={12} aria-hidden="true" /> Create Custom Type
            </button>
          </div>
        </div>
      </section>

      <section className="hr-settings__section">
        <h3 className="hr-settings__title">Paid Holidays</h3>
        <div className="hr-settings__grid">
          {holidays.map(h => (
            <div key={h.id} className="hr-settings__card">
              <div className="hr-settings__card-name">{usDate(h.date)}</div>
              <div className="hr-settings__card-meta">{h.name}</div>
              <div className="hr-settings__card-foot">
                <button type="button" className="hr-settings__delete" disabled={busy} onClick={() => onRemoveHoliday(h.id)} aria-label={`Delete ${h.name}`}>
                  <FiTrash2 size={12} />
                </button>
              </div>
            </div>
          ))}
          <div className="hr-settings__card hr-settings__card--new">
            <input type="date" value={holidayDate} onChange={e => setHolidayDate(e.target.value)} aria-label="Holiday date" />
            <input placeholder="Holiday name" maxLength={100} value={holidayName} onChange={e => setHolidayName(e.target.value)} />
            <button type="button" disabled={busy} onClick={addHoliday}>
              <FiPlus size={12} aria-hidden="true" /> Add Paid Holiday
            </button>
          </div>
        </div>
      </section>

      {licenseAlerts && (
        <section className="hr-settings__section">
          <h3 className="hr-settings__title">License Expiration Alerts</h3>
          <p className="hr-settings__hint">
            Agents' state licenses are tracked in the Directory. Choose how far ahead admins get notified so there's
            time to reach out before a license lapses — the Directory and Dashboard will flag anything inside this window
            and let you create a Momentum Command for it.
          </p>
          <div className="hr-settings__grid">
            <div className="hr-settings__card">
              <div className="hr-settings__card-name">Notify me</div>
              <div className="hr-settings__card-meta">before a license expires</div>
              <select
                className="hr-settings__select"
                value={licenseAlerts.days}
                onChange={ev => licenseAlerts.onChange(Number(ev.target.value))}
                aria-label="Days before expiration to notify"
              >
                {LICENSE_ALERT_OPTIONS.map(d => (
                  <option key={d} value={d}>{d} days</option>
                ))}
              </select>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default HRSettings;
